"use client";

import Link from "next/link";
import { useState } from "react";
import { extractTextFromPdf } from "../lib/pdf/extractText";
import {
  checkSize,
  describeBytes,
  MAX_PDF_BYTES,
} from "../lib/pdf/validateFile";
import { deriveTitle } from "../lib/reading/deriveTitle";
import { chooseTitle, titleFromFileName } from "../lib/reading/fileTitle";
import { newId } from "../lib/reading/newId";
import { readingStore } from "../lib/storage";
import { DuplicateReadingError } from "../lib/storage/types";
import type { PageText } from "../lib/storage/types";

const MIN_LENGTH = 50;
const LABEL = "mb-3 block font-body text-base font-semibold text-paper";

export default function ReadingInput({
  onSaved,
}: {
  onSaved: (readingId: string) => void;
}) {
  const [text, setText] = useState("");
  // What the student calls it. Blank means "use the suggestion".
  const [name, setName] = useState("");
  // The same PDF is already in the library: point at it instead of saving again.
  const [duplicate, setDuplicate] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pages, setPages] = useState<PageText[] | null>(null);
  const [extractedText, setExtractedText] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [reading, setReading] = useState(false);
  // Most students arrive holding a PDF, so uploading is the front door and
  // pasting is the way round it (scanned pages, a web article, a copied
  // chapter). Both stay one click apart.
  const [mode, setMode] = useState<"upload" | "paste">("upload");

  // Pasting and uploading are two ways in, not one form with two fields.
  // Whichever the student chose is the one they see.
  const attached = pdfFile !== null;

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) await accept(file);
    // Cleared so choosing the same file twice in a row still fires a change.
    e.target.value = "";
  }

  async function accept(file: File) {
    setError(null);

    // Checked before a byte is read. Parsing a 200MB file to discover it is
    // too big wastes the student's time and the browser's memory, and the
    // server would only reject it afterwards anyway.
    const size = checkSize(file.size);
    if (!size.ok) {
      setError(size.error);
      return;
    }

    setBusy(true);
    setReading(true);
    const result = await extractTextFromPdf(file);
    setReading(false);
    setBusy(false);
    if (!result.ok) {
      setPdfFile(null);
      setPages(null);
      setExtractedText(null);
      setError(result.error);
      return;
    }
    // The extracted text is kept, never shown. Syft reads the PDF; it does
    // not hand the student back a stripped copy of their own document, and
    // the scan they are about to watch runs over the real pages.
    setPdfFile(file);
    setName((current) => current || titleFromFileName(file.name));
    setPages(result.pages);
    setExtractedText(result.text);
  }

  function removePdf() {
    setMode("upload");
    setPdfFile(null);
    setPages(null);
    setExtractedText(null);
    setName("");
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = (attached ? (extractedText ?? "") : text).trim();
    if (trimmed.length < MIN_LENGTH) {
      setError("Please add more text, at least a few sentences.");
      return;
    }
    setError(null);
    setBusy(true);
    const reading = {
      id: newId(),
      title: chooseTitle(
        name,
        attached && pdfFile
          ? titleFromFileName(pdfFile.name)
          : deriveTitle(trimmed),
      ),
      sourceText: trimmed,
      createdAt: Date.now(),
      ...(pdfFile ? { pdfBlob: pdfFile } : {}),
      ...(pdfFile && pages ? { pages } : {}),
    };
    try {
      await readingStore.addReading(reading);
    } catch (err) {
      setBusy(false);
      if (err instanceof DuplicateReadingError && err.readingId) {
        setDuplicate({ id: err.readingId, title: err.title });
        return;
      }
      setError("Couldn't save your reading. Please try again.");
      return;
    }
    setBusy(false);
    onSaved(reading.id);
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-10">
      {attached ? (
        <div className="rise-in rounded-[1.75rem] bg-ink-alt p-6 sm:p-8">
          <p className="mb-1 font-body text-base font-semibold text-blue">
            Your PDF
          </p>
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <p className="font-reading text-[17px] text-paper">
              {pdfFile.name}
            </p>
            <button
              type="button"
              onClick={removePdf}
              className="font-body text-sm text-muted-dark underline underline-offset-4 hover:text-paper"
            >
              Remove
            </button>
          </div>
          <p className="mt-2 font-body text-base text-muted-dark">
            {pages?.length ?? 0} {pages?.length === 1 ? "page" : "pages"}. Syft
            will read it as it is.
          </p>
        </div>
      ) : mode === "upload" ? (
        <div>
          <span className={LABEL}>Upload your reading</span>

          {/* A drop target as well as a file picker. The label wraps the
                input so a click anywhere on the panel opens the chooser,
                and keyboard users still reach the real input. */}
          <label
            htmlFor="reading-pdf"
            onDragOver={(e) => {
              e.preventDefault();
              if (!reading) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              if (reading) return;
              const file = e.dataTransfer.files?.[0];
              if (file) void accept(file);
            }}
            className={`group flex cursor-pointer flex-col items-center justify-center rounded-[1.75rem] border-2 border-dashed bg-ink-alt px-6 py-20 text-center sm:py-24 transition-colors ${
              dragging
                ? "border-blue bg-blue/10"
                : "border-paper/25 hover:border-blue"
            } ${reading ? "cursor-wait opacity-60" : ""}`}
          >
            {reading ? (
              <>
                <span
                  className="mb-3 h-1 w-14 animate-processing-pulse rounded-full bg-blue"
                  aria-hidden="true"
                />
                <span role="status" className="font-body text-sm text-paper">
                  Reading your PDF
                </span>
              </>
            ) : (
              <>
                <span
                  aria-hidden
                  className={`mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-blue font-heading text-3xl text-blue-deep transition-transform duration-300 ${
                    dragging ? "-translate-y-2" : "group-hover:-translate-y-1"
                  }`}
                >
                  ↑
                </span>
                <span className="font-body text-xl font-semibold text-paper">
                  {dragging
                    ? "Drop it here"
                    : "Drop a PDF here, or choose a file"}
                </span>
                <span className="mt-3 max-w-md font-body text-base text-muted-dark">
                  Up to {describeBytes(MAX_PDF_BYTES)}. PDFs with selectable
                  text; scanned pages are not supported yet.
                </span>
              </>
            )}
            {/* Named explicitly: the visible text in this panel changes
                  while dragging and while reading, so it cannot be the
                  accessible name. */}
            <input
              id="reading-pdf"
              type="file"
              aria-label="Upload a PDF"
              accept="application/pdf,.pdf"
              onChange={handleFileChange}
              disabled={busy}
              className="sr-only"
            />
          </label>

          <p className="mt-6 font-body text-base text-muted-dark">
            Have text instead of a file?{" "}
            <button
              type="button"
              onClick={() => setMode("paste")}
              className="font-semibold text-blue underline underline-offset-4 hover:opacity-80"
            >
              Paste the text instead
            </button>
          </p>
        </div>
      ) : (
        <div>
          <label htmlFor="reading-text" className={LABEL}>
            Paste your reading
          </label>
          <textarea
            id="reading-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={14}
            placeholder="Paste the full text of the reading here."
            className="w-full rounded-[1.75rem] border-2 border-transparent bg-ink-alt p-6 font-reading text-[18px] leading-[1.65] text-paper placeholder:text-muted-dark focus:border-blue"
          />

          <p className="mt-6 font-body text-base text-muted-dark">
            Have a PDF?{" "}
            <button
              type="button"
              onClick={() => setMode("upload")}
              className="font-semibold text-blue underline underline-offset-4 hover:opacity-80"
            >
              Upload it instead
            </button>
          </p>
        </div>
      )}

      {duplicate && (
        <p
          role="alert"
          className="rounded-2xl bg-paper/10 p-4 font-body text-base text-paper"
        >
          You already have this reading
          {duplicate.title ? `, “${duplicate.title}”` : ""}.{" "}
          <Link
            href={`/reading/${duplicate.id}`}
            className="font-semibold text-blue underline underline-offset-4"
          >
            Open it
          </Link>
        </p>
      )}

      {error && (
        <p
          role="alert"
          className="rounded-2xl bg-red/15 p-4 font-body text-base text-paper"
        >
          {error}
        </p>
      )}

      {(attached || mode === "paste") && (
        <label className="flex max-w-xl flex-col gap-2">
          <span className="font-body text-base font-semibold text-paper">
            Name this reading
          </span>
          <input
            type="text"
            value={name}
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
            placeholder={
              attached
                ? undefined
                : "Optional. Syft will use the first line otherwise."
            }
            className="border-2 border-transparent bg-ink-alt px-5 py-3.5 font-body text-lg text-paper placeholder:text-muted-dark focus:border-blue"
          />
        </label>
      )}

      {(attached || mode === "paste") && (
        <button
          type="submit"
          disabled={busy}
          className="self-start bg-blue px-6 py-3.5 font-body text-sm text-blue-deep transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          Use this reading
        </button>
      )}
    </form>
  );
}
